import { AllCommunityModule, ModuleRegistry, themeQuartz, type ColDef, type GridApi, type GridReadyEvent,
  type ICellRendererParams, type ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useApi } from "../api";
import { messagePreview } from "../emailText";
import type { PortalConfig } from "../config";
import { dateTime, dayOnly } from "../format";
import { BellOffIcon, CardIcon, MailIcon, TextIcon } from "../icons";
import type { Lookups, TicketList, TicketRow } from "../types";
import { BulkActions } from "./BulkActions";

ModuleRegistry.registerModules([AllCommunityModule]);

const theme = themeQuartz.withParams({
  accentColor: "var(--accent)",
  backgroundColor: "var(--panel)",
  foregroundColor: "var(--text)",
  borderColor: "var(--line)",
  headerBackgroundColor: "var(--panel)",
  headerTextColor: "var(--muted)",
  rowHoverColor: "var(--accent-soft)",
  // Every other row very slightly grey, for easier reading across.
  oddRowBackgroundColor: "color-mix(in srgb, var(--text) 3%, var(--panel))",
  selectedRowBackgroundColor: "var(--accent-soft)",
  fontFamily: "inherit",
  fontSize: 14,
  headerFontSize: 13,
  rowHeight: 40,
  wrapperBorderRadius: 8,
});

const STATE_KEY = "wrp.ticketGrid.columns.v1";

/** Today's date in Central time as YYYY-MM-DD. */
export function centralToday(now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/** Due date colour as in the old portal: red when past, orange when due today. */
export function dueClass(due: string | null, now = new Date()): string {
  if (!due) return "";
  const today = centralToday(now);
  const d = due.slice(0, 10);
  return d < today ? "due-late" : d === today ? "due-soon" : "";
}

function TicketLink(p: ICellRendererParams<TicketRow>) {
  return p.data ? <Link to={`/tickets/${p.data.ticketNo}`}>{p.data.ticketNo}</Link> : null;
}

function MessageCell(p: ICellRendererParams<TicketRow>) {
  const r = p.data;
  if (!r) return null;
  return (
    <span className="msg-cell">
      {r.unreadSource === "text" ? <span className="icon unread" title="Unread customer text"><TextIcon /></span>
        : r.unreadSource === "message" ? <span className="icon unread" title="Unread customer message"><MailIcon /></span>
        : r.unreadSource === "manual" ? <span className="icon unread dot-icon" title="Marked unread by staff" /> : null}
      {r.ignoreReminders && <span className="icon off" title="Reminders are off for this ticket"><BellOffIcon /></span>}
      {r.unreadPreview && <span className="preview">{r.unreadPreview}</span>}
    </span>
  );
}

function CardCell(p: ICellRendererParams<TicketRow>) {
  return p.data?.cardOnFile ? <span className="icon card" title="Card on file"><CardIcon /></span> : null;
}

/** Ticket list: a grid like the old portal's (multi-select, sort and filter per column). */
export function TicketsPage({ config, lookups }: { config: PortalConfig; lookups: Lookups | null }) {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const status = params.get("status") ?? "";
  const location = params.get("location") ?? "";
  const view = params.get("view") === "all" ? "all" : "queue";

  const [quick, setQuick] = useState(q);
  useEffect(() => setQuick(q), [q]);
  const [selected, setSelected] = useState<TicketRow[]>([]);
  const gridRef = useRef<GridApi<TicketRow> | null>(null);

  const path = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status) p.set("status", status);
    if (location) p.set("location", location);
    p.set("view", view);
    return `/tickets?${p.toString()}`;
  }, [q, status, location, view]);
  const [reload, setReload] = useState(0);
  const { data: raw, error, loading } = useApi<TicketList>(config, path, reload);
  // The API sends the start of the newest unread message as stored; show only what the customer wrote.
  const data = useMemo(() => raw && { ...raw, rows: raw.rows.map((r) => ({ ...r, unreadPreview: r.unreadSource === "manual" ? r.unreadPreview : messagePreview(r.unreadPreview) })) }, [raw]);

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next);
  };

  const columns = useMemo<ColDef<TicketRow>[]>(() => [
    { field: "ticketNo", headerName: "Ticket No.", width: 120, cellRenderer: TicketLink, filter: "agNumberColumnFilter" },
    { field: "firstName", headerName: "First Name", width: 120 },
    { field: "lastName", headerName: "Last Name", width: 130 },
    { field: "location", headerName: "Watchmaker", width: 130 },
    { field: "assignedWatchmakers", headerName: "Assigned Watchmakers", width: 170 },
    { field: "status", headerName: "Status", width: 190 },
    {
      field: "unreadAt", headerName: "Last Updated", width: 175, sort: "desc", filter: false, cellDataType: false,
      valueFormatter: (p: ValueFormatterParams<TicketRow, string | null>) => dateTime(p.value),
      comparator: (a: string | null, b: string | null) => (a ?? "").localeCompare(b ?? ""),
    },
    {
      colId: "messages", headerName: "New Message", flex: 1, minWidth: 260, cellRenderer: MessageCell,
      valueGetter: (p) => p.data?.unreadPreview ?? "", tooltipValueGetter: (p) => p.data?.unreadPreview ?? undefined,
    },
    { field: "cardOnFile", headerName: "Payments", width: 110, cellRenderer: CardCell, filter: false, cellDataType: false },
    {
      field: "dueDate", headerName: "Due date", width: 125, filter: false, cellDataType: false,
      valueFormatter: (p: ValueFormatterParams<TicketRow, string | null>) => (p.value ? dayOnly(p.value) : "TBD"),
      cellClass: (p) => dueClass(p.value as string | null),
    },
    { field: "brands", headerName: "Brands", width: 160, hide: true },
    { field: "customerEmail", headerName: "Email", width: 220, hide: true },
    {
      field: "createdAt", headerName: "Created", width: 170, hide: true, filter: false, cellDataType: false,
      valueFormatter: (p: ValueFormatterParams<TicketRow, string>) => dateTime(p.value),
    },
  ], []);

  const defaultColDef = useMemo<ColDef<TicketRow>>(() => ({
    sortable: true, filter: "agTextColumnFilter", resizable: true, suppressHeaderMenuButton: false,
  }), []);

  const onGridReady = useCallback((e: GridReadyEvent<TicketRow>) => {
    gridRef.current = e.api;
    try {
      const saved = window.localStorage.getItem(STATE_KEY);
      if (saved) e.api.applyColumnState({ state: JSON.parse(saved) as never, applyOrder: true });
    } catch { /* storage unavailable: defaults are fine */ }
  }, []);

  const saveColumns = useCallback(() => {
    try {
      const state = gridRef.current?.getColumnState();
      if (state) window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { gridRef.current?.setGridOption("quickFilterText", quick); }, [quick]);

  const statusOptions = lookups?.statuses.filter((s) => s.isActive || s.tickets > 0) ?? [];
  const searchingAll = !!q;

  return (
    <div className="grid-page">
      <form
        className="grid-toolbar"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          update({ q: quick.trim() || null });
        }}
      >
        <input
          type="search"
          value={quick}
          onChange={(e) => {
            setQuick(e.target.value);
            if (!e.target.value && q) update({ q: null });
          }}
          placeholder="Filter this list… press Enter to search all tickets (ticket #, name, email, phone, brand, tracking #)"
          aria-label="Search tickets"
        />
        <select
          value={status || (view === "all" ? "all" : "")}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") update({ status: null, view: null });
            else if (v === "all") update({ status: null, view: "all" });
            else update({ status: v, view: null });
          }}
          aria-label="Status"
        >
          <option value="">Work queue</option>
          <option value="all">All tickets</option>
          <optgroup label="Status">
            {statusOptions.map((s) => (
              <option key={s.id} value={String(s.id)}>{s.name} ({s.tickets.toLocaleString()})</option>
            ))}
          </optgroup>
        </select>
        <select value={location} onChange={(e) => update({ location: e.target.value || null })} aria-label="Location">
          <option value="">All locations</option>
          {lookups?.locations.map((l) => (
            <option key={l.id} value={l.id}>{l.name}{l.kind === "watchmaker" ? " (watchmaker)" : ""}</option>
          ))}
        </select>
      </form>

      <div className="grid-status">
        <span className="muted">
          {error ? <span className="error">{error.message}</span>
            : !data ? "Loading…"
            : <>
                {searchingAll ? <>Search of all tickets for “{q}”: </> : null}
                {data.total.toLocaleString()} ticket{data.total === 1 ? "" : "s"}
                {data.capped ? ` (showing the ${data.rows.length.toLocaleString()} most recent; narrow the search or pick a status)` : ""}
                {loading ? " · updating…" : ""}
              </>}
        </span>
        {searchingAll && <button className="link" onClick={() => update({ q: null })}>Clear search</button>}
        <span className="spacer" />
      </div>

      {selected.length > 0 && (
        <BulkActions
          config={config}
          lookups={lookups}
          selected={selected}
          onDone={() => setReload((r) => r + 1)}
          onExport={() => gridRef.current?.exportDataAsCsv({ onlySelected: true, fileName: "tickets.csv", allColumns: true })}
          onClear={() => gridRef.current?.deselectAll()}
        />
      )}

      <div className="grid-box">
        <AgGridReact<TicketRow>
          theme={theme}
          rowData={data?.rows ?? null}
          columnDefs={columns}
          defaultColDef={defaultColDef}
          getRowId={(p) => p.data.id}
          rowSelection={{ mode: "multiRow", checkboxes: true, headerCheckbox: true, enableClickSelection: false, selectAll: "filtered" }}
          onSelectionChanged={(e) => setSelected(e.api.getSelectedRows())}
          onGridReady={onGridReady}
          onSortChanged={saveColumns}
          onColumnMoved={saveColumns}
          onColumnResized={(e) => { if (e.finished) saveColumns(); }}
          onColumnVisible={saveColumns}
          pagination
          paginationPageSize={50}
          paginationPageSizeSelector={[25, 50, 100, 250]}
          tooltipShowDelay={400}
          overlayNoRowsTemplate="No tickets match."
          loading={!data && !error}
        />
      </div>
    </div>
  );
}
